import { filterDoctorsForBooking } from "./doctorCatalog";

const GENERAL_KEY = "__general__";
export const GENERAL_SERVICE_LABEL = "Общие";
/**
 * Как в витринах топовых клубов: общая услуга в контексте направления
 * («Первичный осмотр · Терапия»), узкая — просто название.
 */
export function formatServiceBookingLabel(
  service,
  specializationId = null,
  specializations = [],
) {
  const name = service?.name?.trim() || "Услуга";
  if (!specializationId) return name;
  if (service?.specialization_id === specializationId) return name;
  const sp =
    specializations.find((s) => s.id === specializationId) ||
    (service?.specialization_id === specializationId
      ? { name: service.specialization_name }
      : null);
  const specName = sp?.name?.trim() || service?.specialization_name?.trim();
  if (!service?.specialization_id && specName) {
    return `${name} · ${specName}`;
  }
  return name;
}

/** Направления, у которых есть тренеры с этой услугой (для подсказки при общей услуге). */
export function getSpecializationsOfferingService(
  serviceId,
  doctors,
  specializations = [],
) {
  const specIds = new Set();
  for (const d of doctors) {
    if (!(d.service_ids || []).includes(serviceId)) continue;
    if (d.specialization_id) specIds.add(d.specialization_id);
  }
  return specializations.filter((sp) => specIds.has(sp.id));
}

/** Подпись направления услуги в каталоге */
export function serviceSpecLabel(sv) {
  if (!sv?.specialization_id) return GENERAL_SERVICE_LABEL;
  const name = sv.specialization_name?.trim();
  return name || "—";
}

export function isGeneralHiddenForSpec(service, specializationId) {
  if (!service || service.specialization_id || !specializationId) return false;
  return (service.hidden_specialization_ids || []).includes(specializationId);
}

/** Услуги выбранного направления + общие (без specialization_id). */
export function getServicesForSpecialization(services, specializationId) {
  if (!specializationId) return [];
  return services.filter((sv) => {
    if (sv.specialization_id === specializationId) return true;
    if (!sv.specialization_id) {
      return !isGeneralHiddenForSpec(sv, specializationId);
    }
    return false;
  });
}

/** Услуга доступна в зале (branch_ids из каталога). */
export function serviceAtBranch(service, branchId) {
  return (
    Boolean(branchId) &&
    Array.isArray(service?.branch_ids) &&
    service.branch_ids.includes(branchId)
  );
}

/** Специализация доступна в зале. */
export function specializationAtBranch(spec, branchId) {
  return (
    Boolean(branchId) &&
    Array.isArray(spec?.branch_ids) &&
    spec.branch_ids.includes(branchId)
  );
}

/** Услуги направления с учётом зала (branch_ids из каталога). */
export function getServicesForSpecializationAtBranch(
  services,
  specializationId,
  branchId,
) {
  let list = getServicesForSpecialization(services, specializationId);
  if (!branchId) return list;
  return list.filter((sv) => serviceAtBranch(sv, branchId));
}

/**
 * Залы, где доступна услуга.
 * При выбранном направлении — только залы, где эту услугу ведёт тренер этой специализации.
 */
export function getBranchesForService(
  serviceId,
  branches,
  services,
  doctors,
  specializationId = null,
) {
  if (!serviceId) return [];

  const active = branches.filter((b) => b.is_active !== false);
  const svc = services.find((s) => s.id === serviceId);

  let list;
  if (svc?.branch_ids?.length) {
    list = active.filter((b) => svc.branch_ids.includes(b.id));
  } else {
    list = active.filter((br) =>
      doctors.some(
        (d) =>
          (d.service_ids || []).includes(serviceId) &&
          Array.isArray(d.branch_ids) &&
          d.branch_ids.includes(br.id),
      ),
    );
  }

  if (!specializationId) return list;

  return list.filter(
    (br) =>
      filterDoctorsForBooking(doctors, {
        serviceId,
        branchId: br.id,
        specializationId,
      }).length > 0,
  );
}

/** Специализации для записи: общие услуги учитываются в каждом направлении. */
export function buildBookingSpecializationOptions(
  specializations,
  bookableServices,
  branchId = null,
) {
  const byId = new Map();

  for (const sp of specializations || []) {
    if (sp.is_active === false) continue;
    const visibleGeneral = bookableServices.filter(
      (sv) => !sv.specialization_id && !isGeneralHiddenForSpec(sv, sp.id),
    ).length;
    byId.set(sp.id, {
      id: sp.id,
      name: sp.name,
      sort_order: sp.sort_order ?? 0,
      count: visibleGeneral,
    });
  }

  for (const sv of bookableServices) {
    if (!sv.specialization_id) continue;
    if (!byId.has(sv.specialization_id)) {
      const visibleGeneral = bookableServices.filter(
        (s) => !s.specialization_id && !isGeneralHiddenForSpec(s, sv.specialization_id),
      ).length;
      byId.set(sv.specialization_id, {
        id: sv.specialization_id,
        name: sv.specialization_name || "Прочее",
        sort_order: 999,
        count: visibleGeneral,
      });
    }
    byId.get(sv.specialization_id).count += 1;
  }

  let options = [...byId.values()]
    .filter((x) => x.count > 0)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  if (!branchId) return options;

  return options
    .filter((sp) => {
      const row = specializations.find((s) => s.id === sp.id);
      if (specializationAtBranch(row, branchId)) return true;
      return (
        getServicesForSpecializationAtBranch(
          bookableServices,
          sp.id,
          branchId,
        ).length > 0
      );
    })
    .map((sp) => ({
      ...sp,
      count: getServicesForSpecializationAtBranch(
        bookableServices,
        sp.id,
        branchId,
      ).length,
    }))
    .filter((sp) => sp.count > 0);
}

/**
 * Каталог в админке: общие услуги дублируются в каждой специализации.
 * mode: "all" | specId | "general"
 */
export function groupServicesForCatalog(
  services,
  specializations = [],
  mode = "all",
) {
  const specOrder = specializations
    .filter((s) => s.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const sortItems = (list) =>
    [...list].sort((a, b) => {
      const ao = a.sort_order ?? 0;
      const bo = b.sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "", "ru");
    });

  const allGeneral = services.filter((sv) => !sv.specialization_id);
  const generalForSpec = (specId) =>
    allGeneral.filter((sv) => !isGeneralHiddenForSpec(sv, specId));
  const general = allGeneral;
  const bySpecId = new Map();
  for (const sv of services) {
    if (!sv.specialization_id) continue;
    if (!bySpecId.has(sv.specialization_id)) bySpecId.set(sv.specialization_id, []);
    bySpecId.get(sv.specialization_id).push(sv);
  }

  if (mode === "general") {
    return general.length
      ? [
          {
            key: GENERAL_KEY,
            title: "Общие услуги",
            items: sortItems(general),
            isGeneral: true,
          },
        ]
      : [];
  }

  if (mode !== "all") {
    const sp = specOrder.find((s) => s.id === mode);
    const specialized = bySpecId.get(mode) || [];
    const items = sortItems([...specialized, ...generalForSpec(mode)]);
    if (!items.length) return [];
    return [
      {
        key: mode,
        title: null,
        items,
        specName: sp?.name || null,
      },
    ];
  }

  const groups = [];
  for (const sp of specOrder) {
    const specialized = bySpecId.get(sp.id) || [];
    if (!specialized.length) continue;
    groups.push({
      key: sp.id,
      title: sp.name,
      items: sortItems(specialized),
    });
  }

  const orphanIds = [...bySpecId.keys()].filter(
    (id) => !specOrder.some((s) => s.id === id),
  );
  for (const id of orphanIds) {
    const specialized = bySpecId.get(id) || [];
    if (!specialized.length) continue;
    groups.push({
      key: id,
      title: specialized[0]?.specialization_name || "Прочее",
      items: sortItems(specialized),
    });
  }

  if (general.length) {
    groups.push({
      key: GENERAL_KEY,
      title: "Общие услуги",
      items: sortItems(general),
      isGeneral: true,
    });
  }

  return groups;
}

/** Группы услуг тренера (вкладка «Услуги»): без дублирования общих по специализациям */
export function groupDoctorServiceRows(rows) {
  const general = rows.filter((r) => !r.specialization_id);
  const bySpec = new Map();
  for (const r of rows) {
    if (!r.specialization_id) continue;
    if (!bySpec.has(r.specialization_id)) bySpec.set(r.specialization_id, []);
    bySpec.get(r.specialization_id).push(r);
  }
  const groups = [];
  for (const [id, items] of bySpec) {
    groups.push({
      key: id,
      title: items[0]?.specialization_name?.trim() || "Услуги направления",
      items,
    });
  }
  groups.sort((a, b) => a.title.localeCompare(b.title, "ru"));
  if (general.length) {
    groups.unshift({
      key: GENERAL_KEY,
      title: "Общие услуги",
      items: general,
    });
  }
  return groups;
}

/** @deprecated Используйте groupServicesForCatalog */
export function groupServicesBySpecialization(services, specializations = []) {
  return groupServicesForCatalog(services, specializations, "all").flatMap(
    (g) =>
      g.sections
        ? g.sections.map((sec) => ({
            key: `${g.key}-${sec.key}`,
            title: g.sections.length > 1 ? `${g.title} · ${sec.title}` : g.title,
            items: sec.items,
          }))
        : [{ key: g.key, title: g.title, items: g.items || [] }],
  );
}

export function countServicesForCatalog(services, specializations = []) {
  const general = services.filter((sv) => !sv.specialization_id).length;
  const m = { all: services.length, general };
  for (const sp of specializations) {
    if (sp.is_active === false) continue;
    const n = services.filter((sv) => sv.specialization_id === sp.id).length;
    const visibleGeneral = services.filter(
      (sv) => !sv.specialization_id && !isGeneralHiddenForSpec(sv, sp.id),
    ).length;
    m[sp.id] = n + visibleGeneral;
  }
  return m;
}
